// A hash router: #/home, #/playground, #/playground/<lesson id>, #/usage,
// #/policies, #/sign-in, #/welcome, and #/ for the signed-out homepage.

import { useEffect, useState } from "preact/hooks";

export type Route =
  | { name: "landing" } | { name: "sign-in" } | { name: "welcome" } | { name: "policies" }
  | { name: "home" } | { name: "usage" } | { name: "playground"; lesson: string | null } | { name: "not-found" };

// The routes that carry nothing beyond their name, by their first path segment.
const PLAIN_ROUTES = new Map<string, Route>([
  ["sign-in", { name: "sign-in" }], ["welcome", { name: "welcome" }], ["policies", { name: "policies" }],
  ["home", { name: "home" }], ["usage", { name: "usage" }],
]);

export function parseRoute(hash: string): Route {
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  const [a, b] = parts;
  if (a === undefined) return { name: "landing" };
  if (a === "playground") return { name: "playground", lesson: b ?? null };
  return PLAIN_ROUTES.get(a) ?? { name: "not-found" };
}

/** Signed-out visitors see only the homepage, the sign-in page and the policies. */
export const isPublic = (r: Route) => r.name === "landing" || r.name === "sign-in" || r.name === "policies";

export const href = (path: string) => `#/${path}`;
export const navigate = (path: string) => { location.hash = `/${path}`; };

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(location.hash));
  useEffect(() => {
    const on = () => setRoute(parseRoute(location.hash));
    addEventListener("hashchange", on);
    return () => removeEventListener("hashchange", on);
  }, []);
  return route;
}
